import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDemoMode } from "@/hooks/useDemoMode";
import { Droplet, Sparkles, ShieldCheck, EyeOff, ShieldAlert, LogIn, UserPlus } from "lucide-react";

const AUTH_IMAGE = "https://images.pexels.com/photos/37066080/pexels-photo-37066080.jpeg";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Er ging iets mis. Probeer opnieuw.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, refresh } = useAuth();
  const { active: demoActive } = useDemoMode();
  const deniedMessage = location.state?.deniedMessage;

  const [mode, setMode] = useState("login"); // "login" | "register"
  const [naam, setNaam] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGuestAccess = async () => {
    // Refresh laadt /auth/me opnieuw op — de backend geeft een guest-user terug
    // wanneer demo-modus AAN staat, waarna ProtectedRoute doorlaat.
    await refresh?.();
    navigate("/dashboard", { replace: true });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (mode === "register" && password.length < 8) {
      setFormError("Wachtwoord moet minstens 8 tekens lang zijn.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "register") {
        await api.post("/auth/register", { naam, email, password });
      } else {
        await api.post("/auth/login", { email, password });
      }
      await refresh?.();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setFormError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="login-page">
      {/* Left side - Auth form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-md space-y-10">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-sky-700 bg-sky-50 border border-sky-100 px-3 py-1.5 rounded-full">
              <Droplet className="h-3.5 w-3.5" />
              Lifjalla Analytics
            </div>
            <h1 className="text-4xl sm:text-5xl tracking-tight font-bold text-slate-900 leading-[1.05]">
              Inzicht in elke<br />druppel omzet.
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              Importeer je CCV Shop bestellingen en zie meteen je omzet, marges en topregio&apos;s.
            </p>
          </div>

          <div className="space-y-4">
            {deniedMessage && (
              <div
                className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800 flex items-start gap-2"
                data-testid="login-denied-message"
              >
                <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Toegang geweigerd</p>
                  <p className="text-xs mt-0.5 leading-relaxed">{deniedMessage}</p>
                </div>
              </div>
            )}
            <Button
              size="lg"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-12 text-base font-medium"
              onClick={handleGoogleLogin}
              data-testid="login-google-btn"
            >
              <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24" fill="none">
                <path d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#FFC107"/>
                <path d="M3.15295 7.3455L6.43845 9.755C7.32745 7.554 9.48045 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15895 2 4.82795 4.1685 3.15295 7.3455Z" fill="#FF3D00"/>
                <path d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.5717 17.5742 13.3035 18.001 12 18C9.39903 18 7.19053 16.3415 6.35853 14.027L3.09753 16.5395C4.75253 19.778 8.11353 22 12 22Z" fill="#4CAF50"/>
                <path d="M21.8055 10.0415H21V10H12V14H17.6515C17.2571 15.1082 16.5467 16.0766 15.608 16.7855L15.6095 16.7845L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z" fill="#1976D2"/>
              </svg>
              Aanmelden met Google
            </Button>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-widest">
                <span className="bg-slate-50 px-3 text-slate-500">of met e-mail</span>
              </div>
            </div>

            {/* E-mail + wachtwoord */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="password-auth-box">
              <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-md p-1">
                <button
                  type="button"
                  className={`text-sm font-medium py-1.5 rounded ${mode === "login" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                  onClick={() => { setMode("login"); setFormError(""); }}
                  data-testid="auth-tab-login"
                >
                  Inloggen
                </button>
                <button
                  type="button"
                  className={`text-sm font-medium py-1.5 rounded ${mode === "register" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}
                  onClick={() => { setMode("register"); setFormError(""); }}
                  data-testid="auth-tab-register"
                >
                  Registreren
                </button>
              </div>
              <form onSubmit={handlePasswordSubmit} className="space-y-3">
                {mode === "register" && (
                  <div>
                    <Label className="text-xs text-slate-600">Naam</Label>
                    <Input
                      value={naam}
                      onChange={(e) => setNaam(e.target.value)}
                      required
                      placeholder="Voor- en achternaam"
                      data-testid="auth-input-naam"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-600">E-mailadres</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="jij@voorbeeld.be"
                    data-testid="auth-input-email"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Wachtwoord</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={mode === "register" ? 8 : undefined}
                    placeholder={mode === "register" ? "Minstens 8 tekens" : "Wachtwoord"}
                    data-testid="auth-input-password"
                  />
                </div>
                {formError && (
                  <p className="text-xs text-rose-600" data-testid="auth-form-error">{formError}</p>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-sky-700 hover:bg-sky-800 text-white"
                  data-testid="auth-submit-btn"
                >
                  {mode === "register" ? <UserPlus className="h-4 w-4 mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                  {submitting ? "Bezig…" : mode === "register" ? "Account aanvragen" : "Inloggen"}
                </Button>
                {mode === "register" && (
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Na registratie wordt je aanvraag handmatig beoordeeld door de eigenaar. Je krijgt toegang met leesrechten zodra je account is goedgekeurd.
                  </p>
                )}
              </form>
            </div>

            {demoActive && (
              <>
                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase tracking-widest">
                    <span className="bg-slate-50 px-3 text-slate-500">of</span>
                  </div>
                </div>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full h-12 text-base font-medium border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                  onClick={handleGuestAccess}
                  data-testid="login-guest-btn"
                >
                  <EyeOff className="h-5 w-5 mr-2" />
                  Verder als gast (demo-modus)
                </Button>
                <p className="text-xs text-amber-700 text-center" data-testid="login-demo-hint">
                  Deze app draait momenteel in demo-modus. Als gast zie je fictieve klantdata en verborgen bedragen — perfect om de dashboards te verkennen.
                </p>
              </>
            )}
            <p className="text-xs text-slate-500 text-center">
              Door verder te gaan ga je akkoord met het gebruik van een veilige sessie-cookie voor authenticatie.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-200">
            <div className="space-y-1.5">
              <Sparkles className="h-4 w-4 text-sky-600" />
              <p className="text-xs font-medium text-slate-700">Razendsnel</p>
              <p className="text-xs text-slate-500">Batch CSV-import</p>
            </div>
            <div className="space-y-1.5">
              <Droplet className="h-4 w-4 text-sky-600" />
              <p className="text-xs font-medium text-slate-700">Margerich</p>
              <p className="text-xs text-slate-500">Per order & product</p>
            </div>
            <div className="space-y-1.5">
              <ShieldCheck className="h-4 w-4 text-sky-600" />
              <p className="text-xs font-medium text-slate-700">Veilig</p>
              <p className="text-xs text-slate-500">Google Auth</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - hero image */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img
          src={AUTH_IMAGE}
          alt="IJslands gletsjerwater"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 via-slate-900/10 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white space-y-3">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-200">
            Pure als gletsjerwater
          </p>
          <p className="text-2xl font-semibold leading-tight max-w-md">
            Dashboards die net zo helder zijn als waar je water vandaan komt.
          </p>
        </div>
      </div>
    </div>
  );
}
